import React, { useMemo, useState } from "react";
import { Form } from "formik";
import FormFieldComponent from "../../collections/shared/FormComponent/FormFieldComponent";
import moment from "moment";
import {
  getCategories,
  getSubCategories,
  getCategoryMapping,
} from "../../collections/design-portal/api/fetchers/user-management/category-management";
import {
  getRequirementType,
  getSubmissionType,
  getCreativeType,
} from "../../collections/design-portal/api/fetchers/requisition-management/requisition-management";
import { SPOC_TYPE_OPTIONS } from "../../collections/design-portal/shared/constants";

const DesignFieldWrapper = ({
  formikData: {
    values,
    errors,
    touched,
    dirty,
    submitForm,
    isValid,
    setFieldValue,
    setValues,
  },
}) => {
  const getSearchOptions = async (
    setIsFetching,
    getFn,
    payLoad,
    setter,
    hasMorePages,
    sendRawData = false
  ) => {
    setIsFetching && setIsFetching(true);
    const { data, totalPages, page } = await getFn(payLoad);
    const searchedData = (data || []).map(({ name, _id }) => {
      return { label: name, value: _id };
    });
    setter(sendRawData ? data : searchedData);
    hasMorePages && hasMorePages(page < totalPages);
    setIsFetching && setIsFetching(false);
  };

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [reqTypeOptions, setReqTypeOptions] = useState([]);
  const [submissionTypeOptions, setSubmissionTypeOptions] = useState([]);
  const [creativeTypeOptions, setCreativeTypeOptions] = useState([]);

  const formConfig = useMemo(() => {
    const getCategoryMappingData = async (subCategory, spoc) => {
      const categoryId = values?.category?.value;
      const subCategoryId = subCategory || values?.subCategory?.value;
      const spocType = spoc || values?.spocType?.value;
      if (categoryId && subCategoryId && spocType) {
        const data = await getCategoryMapping({
          categoryId,
          subCategoryId,
          spocType,
          page: 1,
        });
        setFieldValue("reviewer", data.data[0]?.reviewer);
        setFieldValue("lead", data.data[0]?.lead);
      }
    };
    return [
      [
        {
          control: "select",
          label: "Category",
          name: "category",
          required: true,
          options: categoryOptions,
          handleChange: () => {
            setFieldValue("subCategory", "");
          },
          getAsyncOptions: (searchText, setIsFetching, hasMorePages) => {
            getSearchOptions(
              setIsFetching,
              getCategories,
              { name: searchText },
              setCategoryOptions,
              hasMorePages
            );
          },
          handleInfiniteScroll: async (
            searchText,
            activePage,
            setActivePage,
            setIsFetching,
            hasMorePages
          ) => {
            try {
              await getSearchOptions(
                setIsFetching,
                getCategories,
                { name: searchText, page: activePage + 1 },
                (options) => {
                  setCategoryOptions([...categoryOptions, ...options]);
                },
                hasMorePages
              );
              setActivePage((prev) => prev + 1);
            } catch (e) {}
          },
          width: 40,
          callOnEmptyText: true,
        },
        {
          control: "select",
          label: "Sub Category",
          name: "subCategory",
          required: true,
          options: subCategoryOptions,
          getAsyncOptions: (searchText, setIsFetching, hasMorePages) => {
            values?.category?.value &&
              getSearchOptions(
                setIsFetching,
                getSubCategories,
                { name: searchText, categoryId: values?.category?.value },
                setSubCategoryOptions,
                hasMorePages
              );
          },
          fetchOnKeyChange: "category",
          handleInfiniteScroll: async (
            searchText,
            activePage,
            setActivePage,
            setIsFetching,
            hasMorePages
          ) => {
            try {
              await getSearchOptions(
                setIsFetching,
                getSubCategories,
                {
                  name: searchText,
                  categoryId: values?.category?.value,
                  page: activePage + 1,
                },
                (options) => {
                  setSubCategoryOptions([...subCategoryOptions, ...options]);
                },
                hasMorePages
              );
              setActivePage((prev) => prev + 1);
            } catch (e) {}
          },
          handleChange: (value) => getCategoryMappingData(value),
          theme: {
            container: "mr-0",
          },
          disabled: !values?.category,
          callOnEmptyText: true,
          width: 40,
        },
      ],
      [
        {
          control: "select",
          label: "SPOC Type",
          name: "spocType",
          required: true,
          options: SPOC_TYPE_OPTIONS,
          handleChange: (value) => getCategoryMappingData(null, value),
          width: 40,
        },
        {
          control: "input",
          type: "number",
          min: 1,
          max: 90,
          placeholder: "Quantity (Units)",
          label: "Quantity (Units)",
          name: "quantity",
          onKeyPress: (e) => {
            if (e.key === "-") e.preventDefault();
          },
          required: true,
          theme: {
            fieldWrapper: "w-[100%]",
          },
          width: 40,
        },
      ],
      [
        {
          control: "select",
          label: "Requirement Type",
          name: "requestType",
          required: true,
          options: reqTypeOptions,
          handleChange: () => {
            setValues({
              ...values,
              submissionType: "",
              creativeType: "",
              proposedDeadline: "",
            });
            setSubmissionTypeOptions([]);
            setCreativeTypeOptions([]);
          },
          getAsyncOptions: (searchText, setIsFetching, hasMorePages) => {
            getSearchOptions(
              setIsFetching,
              getRequirementType,
              { name: searchText },
              setReqTypeOptions,
              hasMorePages
            );
          },
          handleInfiniteScroll: async (
            searchText,
            activePage,
            setActivePage,
            setIsFetching,
            hasMorePages
          ) => {
            try {
              await getSearchOptions(
                setIsFetching,
                getRequirementType,
                { name: searchText, page: activePage + 1 },
                (options) => {
                  setReqTypeOptions([...reqTypeOptions, ...options]);
                },
                hasMorePages
              );
              setActivePage((prev) => prev + 1);
            } catch (e) {}
          },
          callOnEmptyText: true,
          theme: {
            container: "mr-0",
          },
          width: 40,
        },
        {
          control: "select",
          label: "Submission Type",
          name: "submissionType",
          fetchOnKeyChange: "requestType",
          required: true,
          callOnEmptyText: true,
          options: submissionTypeOptions,
          handleChange: () => {
            setValues({
              ...values,
              creativeType: "",
              proposedDeadline: "",
            });
            setCreativeTypeOptions([]);
          },
          getAsyncOptions: (searchText, setIsFetching, hasMorePages) => {
            values?.requestType?.value &&
              getSearchOptions(
                setIsFetching,
                getSubmissionType,
                {
                  name: searchText,
                  requirementId: values?.requestType?.value,
                },
                setSubmissionTypeOptions,
                hasMorePages
              );
          },
          handleInfiniteScroll: async (
            searchText,
            activePage,
            setActivePage,
            setIsFetching,
            hasMorePages
          ) => {
            try {
              await getSearchOptions(
                setIsFetching,
                getSubmissionType,
                {
                  name: searchText,
                  requirementId: values?.requestType?.value,
                  page: activePage + 1,
                },
                (options) => {
                  setSubmissionTypeOptions([
                    ...submissionTypeOptions,
                    ...options,
                  ]);
                },
                hasMorePages
              );
              setActivePage((prev) => prev + 1);
            } catch (e) {}
          },
          disabled: !values?.requestType,
          width: 40,
        },
      ],
      [
        {
          control: "select",
          type: "text",
          label: "Creative Type",
          name: "creativeType",
          options: creativeTypeOptions,
          fetchOnKeyChange: "submissionType",
          callOnEmptyText: true,
          handleChange: (value, _, __, option) => {
            const effortsDays = option?.estimatedTaskEffort;
            !!effortsDays &&
              setFieldValue(
                "proposedDeadline",
                moment().add(effortsDays, "days").format("Do MMMM, YYYY")
              );
          },
          getAsyncOptions: async (searchText, setIsFetching, hasMorePages) => {
            if (values?.requestType?.value && values?.submissionType?.value) {
              getSearchOptions(
                setIsFetching,
                getCreativeType,
                {
                  name: searchText,
                  requirementType: values?.requestType?.value,
                  submissionType: values?.submissionType?.value,
                },
                (data) => {
                  setCreativeTypeOptions(
                    data.map(
                      ({
                        creativeType: { label, value },
                        estimatedTaskEffort,
                      }) => {
                        return { label, value, estimatedTaskEffort };
                      }
                    )
                  );
                },
                hasMorePages,
                true
              );
            }
          },
          handleInfiniteScroll: async (
            searchText,
            activePage,
            setActivePage,
            setIsFetching,
            hasMorePages
          ) => {
            try {
              await getSearchOptions(
                setIsFetching,
                getCreativeType,
                {
                  name: searchText,
                  requirementType: values?.requestType?.value,
                  submissionType: values?.submissionType?.value,
                  page: activePage + 1,
                },
                (data) => {
                  setCreativeTypeOptions([
                    ...creativeTypeOptions,
                    ...data.map(
                      ({
                        creativeType: { label, value },
                        estimatedTaskEffort,
                      }) => {
                        return { label, value, estimatedTaskEffort };
                      }
                    ),
                  ]);
                },
                hasMorePages,
                true
              );
              setActivePage((prev) => prev + 1);
            } catch (e) {}
          },
          required: true,
          theme: {
            fieldWrapper: "w-[100%]",
            container: "mr-0",
          },
          disabled: !values?.submissionType,
          width: 40,
        },
        {
          control: "input",
          type: "text",
          label: "Reference URL",
          name: "referenceUrl",
          required: true,
          theme: {
            fieldWrapper: "w-[100%]",
          },
          width: 40,
        },
      ],
      [
        {
          control: "textarea",
          type: "textarea",
          label: "Remarks/Notes",
          name: "remarks",
          required: false,
          theme: {
            fieldWrapper: "w-[100%]",
            container: "mr-0",
          },
          width: 40,
        },
        {
          control: "textarea",
          type: "textarea",
          label: "Design Description",
          name: "description",
          required: true,
          theme: {
            fieldWrapper: "w-[100%]",
          },
          width: 40,
        },
      ],
      [
        {
          control: "input",
          label: "Estimated Delivery Date",
          name: "proposedDeadline",
          required: true,
          theme: {
            fieldWrapper: "w-[100%]",
          },
          disabled: true,
          width: 26,
        },
        {
          control: "select",
          label: "Reviewer",
          name: "reviewer",
          required: true,
          options: [],
          theme: {
            fieldWrapper: "w-[100%]",
          },
          disabled: true,
          width: 26,
        },
        {
          control: "select",
          label: "Design Lead (SPOC)",
          name: "lead",
          required: true,
          options: [],
          theme: {
            fieldWrapper: "w-[100%]",
            container: "mr-0",
          },
          disabled: true,
          width: 26,
        },
      ],
    ];
  }, [
    values,
    values?.creativeType,
    categoryOptions,
    subCategoryOptions,
    reqTypeOptions,
    submissionTypeOptions,
    creativeTypeOptions,
  ]);

  return (
    <Form>
      <FormFieldComponent
        formConfig={formConfig}
        values={values}
        errors={errors}
        touched={touched}
        style={{
          justifyContent: "center",
        }}
      />

      <div className="flex justify-center mb-2 items-start">
        <button
          type="submit"
          disabled={!dirty || !isValid}
          className="m-auto bg-primary text-white px-5 py-2 rounded-xl text-sm disabled:bg-gray-500 disabled:cursor-not-allowed "
        >
          Submit
        </button>
      </div>
    </Form>
  );
};

export default DesignFieldWrapper;
